import { convertKeysToCamelCase } from '@/utils/convertKeysToCamelCase/convertKeysToCamelCase';
import { request } from '@/utils/requestHelpers';

const namespace = '/api/v5';
const rumikoNamespace = '/ms/rumiko/v1';

interface RawApiKey {
  key_id: string;
  state: string;
  created_at: string;
  secret_key?: string;
}

export interface RawFetchCurrentApiKeysResponse {
  api_keys: RawApiKey[];
  meta: {
    status: number;
    free_user: boolean;
    mobile_free: boolean;
    subscription_state: string;
  };
}

interface ApiKey {
  keyId: string;
  state: string;
  createdAt: string;
  secretKey?: string;
}

export interface FetchCurrentApiKeysResponse {
  apiKeys: ApiKey[];
  meta: {
    status: number;
    freeUser: boolean;
    mobileFree: boolean;
    subscriptionState: string;
  };
}

type FetchApiDataPointsResponse = Record<string, number>;

export interface FetchCurrentApiKeyResponse {
  apiKey: ApiKey;
  meta: {
    status: number;
    freeUser: boolean;
    mobileFree: boolean;
    subscriptionState: string;
  };
}

export interface DeleteApiKeyResponse {
  meta: {
    status: number;
    freeUser: boolean;
    mobileFree: boolean;
    subscriptionState: string;
  };
}

export interface ApiKeysRequestsInterface {
  fetchCurrentApiKeys: (token: string) => Promise<FetchCurrentApiKeysResponse>;
  createApiKey: (token: string) => Promise<FetchCurrentApiKeyResponse>;
  enableApiKey: (
    token: string,
    keyId: string
  ) => Promise<FetchCurrentApiKeyResponse>;
  disableApiKey: (
    token: string,
    keyId: string
  ) => Promise<FetchCurrentApiKeyResponse>;
  deleteApiKey: (token: string, keyId: string) => Promise<DeleteApiKeyResponse>;
  fetchApiDataPoints: (token: string) => Promise<FetchApiDataPointsResponse>;
}

const headers = (token: string) => ({
  headers: new Headers({
    'content-type': 'application/json',
    Authorization: `Bearer ${token}`,
  }),
});

const fetchCurrentApiKeys = async (token: string) => {
  const init = {
    ...headers(token),
    method: 'GET',
  };

  const response: RawFetchCurrentApiKeysResponse = await request(
    `${namespace}/api_keys`,
    init
  );
  return convertKeysToCamelCase(response) as FetchCurrentApiKeysResponse;
};

const createApiKey = async (token: string) => {
  const init = {
    ...headers(token),
    method: 'POST',
  };

  const response = await request(`${namespace}/api_keys`, init);
  return convertKeysToCamelCase(response) as FetchCurrentApiKeyResponse;
};

const enableApiKey = async (token: string, keyId: string) => {
  const init = {
    ...headers(token),
    method: 'POST',
  };

  const response = await request(`${namespace}/api_keys/${keyId}/enable`, init);
  return convertKeysToCamelCase(response) as FetchCurrentApiKeyResponse;
};

const disableApiKey = async (token: string, keyId: string) => {
  const init = {
    ...headers(token),
    method: 'POST',
  };

  const response = await request(
    `${namespace}/api_keys/${keyId}/disable`,
    init
  );
  return convertKeysToCamelCase(response) as FetchCurrentApiKeyResponse;
};

const deleteApiKey = async (token: string, keyId: string) => {
  const init = {
    ...headers(token),
    method: 'DELETE',
  };

  const response = await request(`${namespace}/api_keys/${keyId}`, init);
  return convertKeysToCamelCase(response) as DeleteApiKeyResponse;
};

const fetchApiDataPoints = async (token: string) => {
  const init = {
    ...headers(token),
    method: 'GET',
  };

  const response = await request(
    `${rumikoNamespace}/vehicle/custom_datapoints`,
    init
  );

  return response;
};

export const ApiKeysRequests: ApiKeysRequestsInterface = {
  fetchCurrentApiKeys,
  fetchApiDataPoints,
  createApiKey,
  enableApiKey,
  disableApiKey,
  deleteApiKey,
};
